<template>
  <div v-if="loading" class="dare-loading">
    <PageHeader eyebrow="Dare" title="Loading challenge…" />
  </div>

  <div v-else-if="error" class="dare-error">
    <PageHeader eyebrow="Dare" title="Not found" description="This dare link is invalid or has expired." />
    <button class="black-button" @click="router.push({ name: 'home' })">Go home</button>
  </div>

  <TestView
    v-else-if="!dareFinished"
    :injectedChallenges="run.challenges"
    :dareTimePerChallenge="timePerChallengeNum"
    :dareComplexity="run.complexity"
    :dareMode="true"
    @dareComplete="onDareComplete"
  />

  <div v-else class="dare-comparison">
    <PageHeader eyebrow="Dare complete" title="Results" />
    <div class="dare-scores">
      <div class="dare-score-row">
        <span class="dare-score-label">You scored</span>
        <span class="dare-score-value" :class="verdictClass">{{ challengerScore }}</span>
      </div>
      <div class="dare-score-row">
        <span class="dare-score-label">{{ run.displayName || 'Someone' }} scored</span>
        <span class="dare-score-value">{{ run.score }}</span>
      </div>
    </div>
    <p class="dare-verdict" :class="verdictClass">{{ verdictText }}</p>
    <div class="dare-actions">
      <button class="black-button" @click="showShareSheet = true">
        <i class="ri-sword-line"></i>&nbsp;Dare someone else
      </button>
      <button class="black-button" @click="router.push({ name: 'home' })">Play your own run</button>
    </div>

    <ShareSheet
      v-if="showShareSheet"
      :url="shareUrl"
      @close="showShareSheet = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import TestView from '@/components/TestView.vue';
import PageHeader from '@/components/parts/PageHeader.vue';
import ShareSheet from '@/components/parts/ShareSheet.vue';

const props = defineProps({ runId: String });
const router = useRouter();
const { fetchSharedRun, recordDareResult } = useAuth();

const run = ref(null);
const loading = ref(true);
const error = ref(false);
const dareFinished = ref(false);
const challengerScore = ref(null);
const showShareSheet = ref(false);

const timePerChallengeNum = computed(() =>
  run.value ? Number(run.value.timePerChallenge) : 30
);

const verdictText = computed(() => {
  if (challengerScore.value > run.value?.score) return 'You win!';
  if (challengerScore.value < run.value?.score) return 'They win.';
  return "It's a tie!";
});

const verdictClass = computed(() => {
  if (challengerScore.value > run.value?.score) return 'verdict-win';
  if (challengerScore.value < run.value?.score) return 'verdict-loss';
  return 'verdict-tie';
});

const shareUrl = computed(() =>
  `${window.location.origin}${window.location.pathname}#/dare/${props.runId}`
);

onMounted(async () => {
  try {
    const data = await fetchSharedRun(props.runId);
    if (!data || !data.challenges) {
      error.value = true;
    } else {
      run.value = data;
    }
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
});

function onDareComplete({ score, correctPct }) {
  challengerScore.value = score;
  dareFinished.value = true;
  recordDareResult(props.runId, score, correctPct).catch(() => {});
}
</script>

<style scoped>
.dare-loading,
.dare-error {
  padding: 20px 0;
}

.dare-error .black-button {
  margin-top: 16px;
}

.dare-comparison {
  padding: 20px 0;
}

.dare-scores {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0;
}

.dare-score-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.dare-score-label {
  font-size: 14px;
  color: var(--text-color);
  min-width: 160px;
}

.dare-score-value {
  font-size: 28px;
  font-weight: bold;
  color: var(--text-color-bright);
}

.dare-verdict {
  font-size: 18px;
  font-weight: bold;
  margin: 8px 0 24px;
}

.verdict-win  { color: #4caf50; }
.verdict-loss { color: var(--geogaddi-r); }
.verdict-tie  { color: var(--geogaddi-y); }

.dare-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
